export const APP_NAME = 'Eurobath ERP';
export const APP_VERSION = '1.0.0';
export const COMPANY_FALLBACK = 'Eurobath';

export const LOCALES = ['es', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'es';

export const TERMINAL_KINDS = ['admin', 'purchasing', 'warehouse', 'packing', 'logistics', 'management'] as const;
export type TerminalKind = (typeof TERMINAL_KINDS)[number];
export const TERMINAL_KIND_LABELS: Record<TerminalKind, string> = {
  admin: 'Administración',
  purchasing: 'Compras',
  warehouse: 'Almacén',
  packing: 'Embalaje',
  logistics: 'Logística',
  management: 'Dirección'
};

/**
 * Cada tipo de terminal habilita únicamente los módulos de su puesto.
 * El backend lo comprueba además del RBAC; la UI lo usa para navegación y landing.
 */
export const TERMINAL_SCOPES: Record<TerminalKind, readonly string[]> = {
  admin: ['*'],
  // Tesorería es de compras y dirección: quien paga facturas necesita ver las cuentas.
  purchasing: ['products', 'suppliers', 'purchases', 'stock', 'mirrors', 'bom', 'planning', 'dashboard', 'finance'],
  warehouse: ['products', 'stock', 'kits', 'orders', 'mirrors', 'bom', 'packaging', 'purchases', 'planning', 'dashboard'],
  packing: ['products', 'stock', 'kits', 'packaging', 'orders', 'mirrors', 'planning', 'dashboard'],
  logistics: ['products', 'stock', 'kits', 'packaging', 'logistics', 'orders', 'planning', 'dashboard'],
  management: ['products', 'stock', 'purchases', 'orders', 'kits', 'mirrors', 'bom', 'packaging', 'logistics', 'planning', 'dashboard', 'audit', 'finance']
};

export function terminalAllows(kind: string | null | undefined, module: string): boolean {
  if (!kind || kind === 'admin') return true;
  const scope = TERMINAL_SCOPES[kind as TerminalKind];
  return !scope || scope.includes('*') || scope.includes(module);
}

export const TERMINAL_NAV: Record<TerminalKind, readonly string[]> = {
  admin: ['*'],
  purchasing: ['dashboard', 'purchase-orders', 'invoices', 'suppliers', 'products', 'planning', 'treasury'],
  // La recepción de mercancía se hace en el puesto de almacén.
  warehouse: ['dashboard', 'warehouse', 'kits', 'products', 'stock', 'purchase-orders', 'invoices'],
  packing: ['packaging'],
  logistics: ['logistics'],
  management: ['dashboard', 'planning', 'treasury', 'admin']
};

export function terminalShowsNav(kind: string | null | undefined, key: string): boolean {
  if (!kind) return true;
  const nav = TERMINAL_NAV[kind as TerminalKind];
  return !nav || nav.includes('*') || nav.includes(key);
}

/** Puestos de fábrica: pantalla completa, sin menús de oficina y con objetivos táctiles. */
export const STATION_KINDS = ['warehouse', 'packing', 'logistics'] as const;

/**
 * Armazón que corresponde a un terminal. Manda el puesto, no la persona: el mismo jefe de
 * almacén en un PC de oficina ve el armazón de escritorio y en el puesto de fábrica, el de
 * puesto. Sin terminal (o con uno de administración) se usa el de escritorio.
 */
export function terminalLayout(kind: string | null | undefined): 'station' | 'desktop' {
  return (STATION_KINDS as readonly string[]).includes(kind ?? '') ? 'station' : 'desktop';
}

export const TERMINAL_HOME: Record<TerminalKind, string> = {
  admin: '/',
  purchasing: '/invoices',
  warehouse: '/warehouse',
  packing: '/packaging',
  logistics: '/logistics',
  management: '/planning'
};

export const PRODUCT_TYPES = ['raw_material', 'component', 'consumable', 'semi_finished', 'packaging', 'finished_good'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];
export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  raw_material: 'Materia prima',
  component: 'Componente',
  consumable: 'Consumible',
  semi_finished: 'Semielaborado',
  packaging: 'Embalaje',
  finished_good: 'Producto terminado'
};

export const UNIT_KINDS = ['unit', 'length', 'area', 'weight', 'volume'] as const;
export type UnitKind = (typeof UNIT_KINDS)[number];
export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  unit: 'Unidad',
  length: 'Longitud',
  area: 'Superficie',
  weight: 'Peso',
  volume: 'Volumen'
};
export const UNIT_KIND_SHORT: Record<UnitKind, string> = {
  unit: 'ud', length: 'm', area: 'm²', weight: 'kg', volume: 'L'
};

export const MOVEMENT_REASONS = [
  'initial', 'reception', 'kit_consumption', 'packaging_consumption', 'adjustment',
  'transfer_in', 'transfer_out', 'production_output', 'return', 'scrap'
] as const;
export type MovementReason = (typeof MOVEMENT_REASONS)[number];
export const MOVEMENT_REASON_LABELS: Record<MovementReason, string> = {
  initial: 'Stock inicial',
  reception: 'Recepción de compra',
  kit_consumption: 'Consumo de kit',
  packaging_consumption: 'Consumo de embalaje',
  adjustment: 'Regularización',
  transfer_in: 'Transferencia entrada',
  transfer_out: 'Transferencia salida',
  production_output: 'Producción',
  return: 'Devolución',
  scrap: 'Merma / desecho'
};

export const BOM_RULE_TYPES = ['fixed', 'perimeter', 'area', 'led_length', 'power_selector', 'formula'] as const;
export type BomRuleType = (typeof BOM_RULE_TYPES)[number];
export const BOM_RULE_LABELS: Record<BomRuleType, string> = {
  fixed: 'Cantidad fija',
  perimeter: 'Perímetro + merma',
  area: 'Superficie + merma',
  led_length: 'Longitud de LED',
  power_selector: 'Selección por potencia',
  formula: 'Fórmula'
};

export const BOM_STATUSES = ['draft', 'active', 'retired'] as const;
export type BomStatus = (typeof BOM_STATUSES)[number];
export const BOM_STATUS_LABELS: Record<BomStatus, string> = {
  draft: 'Borrador', active: 'Activa', retired: 'Retirada'
};

export const SALES_ORDER_STATUSES = ['draft', 'confirmed', 'in_production', 'partially_shipped', 'shipped', 'cancelled'] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];
export const SALES_ORDER_STATUS_LABELS: Record<SalesOrderStatus, string> = {
  draft: 'Borrador', confirmed: 'Confirmado', in_production: 'En producción',
  partially_shipped: 'Parcialmente expedido', shipped: 'Expedido', cancelled: 'Cancelado'
};

export const MIRROR_UNIT_STATUSES = ['created', 'kit_pending', 'kit_ready', 'in_production', 'packed', 'ready_to_ship', 'shipped', 'cancelled'] as const;
export type MirrorUnitStatus = (typeof MIRROR_UNIT_STATUSES)[number];
export const MIRROR_UNIT_STATUS_LABELS: Record<MirrorUnitStatus, string> = {
  created: 'Creada', kit_pending: 'Kit pendiente', kit_ready: 'Kit preparado',
  in_production: 'En producción', packed: 'Embalada', ready_to_ship: 'Lista para logística',
  shipped: 'Expedida', cancelled: 'Cancelada'
};

export const KIT_STATUSES = ['pending', 'in_progress', 'blocked', 'completed', 'cancelled'] as const;
export type KitStatus = (typeof KIT_STATUSES)[number];
export const KIT_STATUS_LABELS: Record<KitStatus, string> = {
  pending: 'Pendiente', in_progress: 'En preparación', blocked: 'Bloqueado',
  completed: 'Completado', cancelled: 'Cancelado'
};

export const INVOICE_STATUSES = ['pending_review', 'validated', 'rejected'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  pending_review: 'Pendiente de revisión', validated: 'Validada', rejected: 'Rechazada'
};

export const PURCHASE_ORDER_STATUSES = ['draft', 'confirmed', 'partially_received', 'received', 'cancelled'] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];
export const PURCHASE_ORDER_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  draft: 'Borrador', confirmed: 'Confirmado', partially_received: 'Parcialmente recibido',
  received: 'Recibido', cancelled: 'Cancelado'
};

export const SCHEDULE_STATUSES = ['pending', 'reserved', 'released', 'shipped', 'cancelled'] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];
export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  pending: 'Pendiente', reserved: 'Reservado', released: 'Liberado',
  shipped: 'Expedido', cancelled: 'Cancelado'
};

export const PACKAGING_ORDER_STATUSES = ['pending', 'in_progress', 'done', 'cancelled'] as const;
export type PackagingOrderStatus = (typeof PACKAGING_ORDER_STATUSES)[number];
export const PACKAGING_ORDER_STATUS_LABELS: Record<PackagingOrderStatus, string> = {
  pending: 'Pendiente', in_progress: 'En fabricación', done: 'Completado', cancelled: 'Cancelado'
};

export const PANOTEC_STATUSES = ['queued', 'sent', 'done', 'error'] as const;
export type PanotecStatus = (typeof PANOTEC_STATUSES)[number];
export const PANOTEC_STATUS_LABELS: Record<PanotecStatus, string> = {
  queued: 'En cola', sent: 'Enviado', done: 'Completado', error: 'Error'
};

export const SHIPMENT_STATUSES = ['draft', 'label_generated', 'in_transit', 'delivered', 'cancelled'] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];
export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  draft: 'Borrador', label_generated: 'Etiqueta generada', in_transit: 'En tránsito',
  delivered: 'Entregado', cancelled: 'Cancelado'
};

export const ALERT_TYPES = ['low_stock', 'critical_stock', 'projected_shortage', 'lead_time_risk'] as const;
export type AlertType = (typeof ALERT_TYPES)[number];
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  low_stock: 'Stock bajo', critical_stock: 'Stock crítico',
  projected_shortage: 'Rotura prevista', lead_time_risk: 'Riesgo de plazo'
};

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];
export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info: 'Aviso', warning: 'Advertencia', critical: 'Crítico'
};

export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];
export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  open: 'Abierta', acknowledged: 'Revisada', resolved: 'Resuelta'
};

export const RESERVATION_STATUSES = ['active', 'consumed', 'released'] as const;
export const PACKAGING_MODES = ['fixed', 'parametric'] as const;
export type PackagingMode = (typeof PACKAGING_MODES)[number];

export const CUSTOMER_TYPES = ['company', 'freelancer'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

/** Régimen de IVA del cliente: cambia el tratamiento de la factura, no la dirección. */
export const VAT_REGIMES = ['general', 'intracomunitario', 'exportacion', 'canarias_ceuta_melilla'] as const;
export type VatRegime = (typeof VAT_REGIMES)[number];

/** Tipos de anotación del seguimiento de un cliente. */
export const CUSTOMER_NOTE_KINDS = ['note', 'call', 'visit', 'incident', 'claim'] as const;
export type CustomerNoteKind = (typeof CUSTOMER_NOTE_KINDS)[number];
export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  company: 'Empresa',
  freelancer: 'Autónomo'
};

export const ROLES_CATALOG = [
  { code: 'admin', name: 'Administrador' },
  { code: 'purchasing', name: 'Compras' },
  { code: 'warehouse', name: 'Almacén' },
  { code: 'packing', name: 'Embalaje' },
  { code: 'logistics', name: 'Logística' },
  { code: 'management', name: 'Dirección' }
] as const;

export const PAGE_SIZES = [10, 20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 20;
